'use client';
import { useState } from 'react';
import Link from 'next/link';
import { HiOutlinePrinter, HiOutlineTrash } from 'react-icons/hi';
import { AiFillInfoCircle } from 'react-icons/ai';

import useMyTestsState from 'src/hooks/useMyTestsState';
import { appointmentUrl, testUrl } from 'src/services/route-utils';
import Spinner from './Spinner';
import PagePrint from './PagePrint';
import Tooltip from './Tooltip';
import InfoTooltip from './InfoTooltip';

type Props = {
    title?: string;
    asContent?: boolean;
    withLink?: boolean;
    withPrint?: boolean;
};

export default function MyTestsSummary({
    title = 'Sumar selecție analize',
    asContent = false,
    withLink = false,
    withPrint = true,
}: Props) {
    const { tests, total, tax, loaded, removeTest } = useMyTestsState();
    const [print, setPrint] = useState(false);

    if (asContent) {
        const withDisclaimer = tests
            ?.filter((test) => !!test.disclaimer)
            ?.map((test) => (
                <div key={test.slug} className="text-third">
                    {test.name} - {test.disclaimer}
                </div>
            ));

        return (
            <>
                {tests?.map((test) => test.name)?.join(', ')}
                {!!withDisclaimer?.length && withDisclaimer}
            </>
        );
    }

    return (
        <>
            <div className="flex items-center gap-4">
                <div className="font-display flex-1 pb-4 text-xl md:text-2xl">{title}</div>
                {withPrint && !!tests?.length && (
                    <>
                        <PagePrint
                            pageSrc="/formular-analizele-mele"
                            print={print}
                            afterPrint={() => setPrint(false)}
                        />
                        <HiOutlinePrinter
                            title="Listează analizele"
                            aria-label="Listează analizele"
                            role="button"
                            className="text-primary hover:text-primary/75 hidden cursor-pointer text-xl lg:inline-block"
                            onClick={() => setPrint(true)}
                        />
                    </>
                )}
            </div>

            {loaded ? (
                <>
                    {!tests.length && (
                        <div className="bg-medium-grey/20 -mx-4 mb-4 p-4 text-sm md:mx-0">
                            Nu aveți analize selectate.
                        </div>
                    )}
                    {!!tests?.length && (
                        <>
                            <ul className="border-dark-green/20 border-y py-2">
                                {tests.map((test) => (
                                    <li key={test.slug} className="flex items-center gap-2 py-2 text-sm md:text-base">
                                        <div className="flex-1 ">
                                            <Link className="hover:underline" href={testUrl(test)}>
                                                {test.name}
                                            </Link>
                                            <InfoTooltip title={test?.disclaimer} />
                                        </div>
                                        <div>{test.price} LEI</div>
                                        <div className="w-4">
                                            <HiOutlineTrash
                                                className="text-primary hover:text-third cursor-pointer text-xl"
                                                aria-hidden
                                                role="button"
                                                aria-label="Șterge analiza"
                                                onClick={() => removeTest(test.slug)}
                                            />
                                        </div>
                                    </li>
                                ))}
                                {!!tax && (
                                    <li className="flex items-center gap-2 py-2 text-sm md:text-base">
                                        <div className="flex-1">Taxă de recoltare</div>
                                        <div>{tax} LEI</div>
                                        <div className="w-4" />
                                    </li>
                                )}
                            </ul>
                            <div className="flex gap-2 py-4 text-lg md:text-xl">
                                <div className="font-display flex-1">
                                    Total{' '}
                                    <Tooltip
                                        position="right"
                                        title="Lista de analize are scop orientativ; vei putea adăuga/elimina analize odată ajuns/ă în locație; plata se face la finalul recoltării. ">
                                        <AiFillInfoCircle className="inline align-baseline text-base" />
                                    </Tooltip>
                                </div>
                                <div>{total} LEI</div>
                                <div className="w-4" />
                            </div>
                        </>
                    )}

                    {withLink && !!tests.length && (
                        <Link
                            href={appointmentUrl()}
                            className="btn btn-primary btn-md mx-auto mt-4 block w-fit text-center">
                            Finalizează programarea online
                        </Link>
                    )}
                </>
            ) : (
                <div className="my-4 flex items-center justify-center">
                    <Spinner />
                </div>
            )}
        </>
    );
}
